import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { kebabCase } from 'lodash-es';
import FocusLock, { AutoFocusInside } from 'react-focus-lock';

import Icon from '@klv/atoms/icon';
import { useTranslation } from '@klv/hooks/useTranslation';
import Portal from '@klv/shared/utils/portal';
import { generateClassName } from '@klv/shared/utils/functions';
import { getBackgroundImage, isGradient, getTextColor, getBackgroundColor } from '@klv/utils/wrapper/functions';

import * as s from './styles/modal.module.scss';

import type { MouseEvent } from 'react';
import type { IConditionalGrid, IKlaviyoModal, IModalContentsWrapper } from './types';

const ConditionalGrid = ({
  children, contentPadding = '', innerRef, size, ...rest
}: IConditionalGrid) => {
  if (size === 'constrained') {
    return (
      <div
        className={generateClassName(['grid', s.constrainedGrid])}
        ref={innerRef}
        style={contentPadding ? {
          '--modal-content-padding': contentPadding,
        } as React.CSSProperties : undefined}
        {...rest}
      >
        {children}
      </div>
    );
  }

  return children;
};



const ModalContentsWrapper = ({
  animate,
  animateHide,
  backgroundColor = 'transparent',
  children,
  className,
  isMediaModal,
  ...rest
}: IModalContentsWrapper) => (isMediaModal ? (
  <div
  style={{
    '--modal-background-image': getBackgroundImage(backgroundColor),
    '--modal-color': `var(--color-${getTextColor(backgroundColor)})`,
    '--modal-background-color':  `var(--color-${kebabCase(getBackgroundColor(backgroundColor))})`,
  } as React.CSSProperties}
  className={generateClassName([
    'grid',
    className,
    s.modalContents,
    s.mediaModalContents,
    animate && s.animate,
    animateHide && s.animateOut,
    isGradient(backgroundColor) ? s.gradient : '',
  ])}
  {...rest}
  >
    {children}
  </div>
) : (
  <div
  className={generateClassName([
    'grid',
    className,
    s.modalContents,
    animate && s.animate,
    animateHide && s.animateOut,
  ])}
  {...rest}
  >
    {children}
  </div>
));

const ModalCloseButton = ({ className, close, isMediaModal }: { className?: string; close: () => void; isMediaModal: boolean; }) => {
  const { t } = useTranslation();

  return (
    <button
      aria-label={t('organisms_modal_closeModal')}
      className={generateClassName(['modalCloseButton', className, s.closeButton, isMediaModal && s.isMediaModal])}
      onClick={close}
      type="button"
    >
      <Icon className={s.closeIcon} color="currentColor" iconName="PlusCircle" />
      <Icon className={s.closeIconHover} color="currentColor" iconName="PlusCircleFilled" />
    </button>
  );
};

const KlaviyoModal = ({
  backgroundColor = 'charcoal',
  children,
  className,
  close,
  contentPadding,
  show,
  size = 'full',
  isMediaModal = false,
  title,
  ...rest
}: IKlaviyoModal) => {
  const [animateHide, setAnimateHide] = useState(false);
  const [activeTimeout, setActiveTimeout] = useState(0);
  
  const modalRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const isConstrained = size === 'constrained';
  
  const onCloseModal = useCallback(() => {
    if (isConstrained) {
      setAnimateHide(true);
      
      if (activeTimeout) {
        return;
      }
      
      setActiveTimeout(window.setTimeout(() => {
        close();
        setAnimateHide(false);
        setActiveTimeout(0);
      }, 500)); // this timeout should match fade-out animation duration
    } else {
      close();
    }
  }, []);
  
  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onCloseModal();
      }
    }
    
    if (show) {
      document.body.setAttribute('data-modal', 'open');
      window.addEventListener('keydown', onKeydown);
    }
    
    return () => {
      document.body.setAttribute('data-modal', 'closed');
      window.removeEventListener('keydown', onKeydown);
    };
  }, [show, onCloseModal]);
  
  const handleClick = (event: MouseEvent) => {
    const target = event?.target as HTMLElement;
    if (target === modalRef?.current || target === gridRef?.current) {
      onCloseModal();
    }
  };
  
  return (
    <Portal>
      {show && (
        <>
          <FocusLock whiteList={(node) => document.getElementById('portal')?.contains(node) || false}>
            <AutoFocusInside>
              <div
                aria-modal="true"
                className={generateClassName([className, 'klaviyoModal', s.modal, isConstrained && s.animate])}
                onClick={(isConstrained) ? handleClick : undefined}
                ref={modalRef}
                role="dialog"
                {...rest}
              >
                <ConditionalGrid contentPadding={contentPadding} innerRef={gridRef} size={size}>
                  <ModalContentsWrapper 
                    animate={isConstrained} 
                    animateHide={animateHide} 
                    backgroundColor={backgroundColor}
                    className={generateClassName(['modalContents'])}
                    isMediaModal={isMediaModal}
                    
                  >
                    {title && (
                      <h2 className={generateClassName(['h4', s.title])}>
                        {title}
                      </h2>
                    )}
                    <ModalCloseButton close={() => onCloseModal()} isMediaModal={isMediaModal} />
                    <div className={generateClassName(['modalBody', s.modalBody, size === 'full' && s.full])}>
                      {children}
                    </div>
                  </ModalContentsWrapper>
                </ConditionalGrid>
              </div>
            </AutoFocusInside>
          </FocusLock>
          {(size === 'constrained') && (
            <div
              className={generateClassName([
                s.overlayBlur,
                s.animateIn,
                animateHide && s.animateOut,
              ])}
            />
          )}
        </>
      )}
    </Portal>
  );
};

export default KlaviyoModal;
